import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;

const Page = styled.div`
  min-height: 100vh; background: #0a0a0f; color: #e2e8f0; font-family: 'Inter', sans-serif; padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid #1e2030;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer; padding: 8px; border-radius: 8px;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const TitleArea = styled.div`flex: 1;`;
const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #f43f5e, #fb923c); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const NavTabs = styled.div`display: flex; gap: 8px;`;
const NavTab = styled.button`
  background: ${p => p.active ? 'rgba(244,63,94,0.15)' : 'transparent'};
  border: 1px solid ${p => p.active ? '#f43f5e' : '#1e2030'};
  color: ${p => p.active ? '#f43f5e' : '#64748b'};
  padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer;
  &:hover { border-color: #f43f5e; color: #f43f5e; }
`;

const Table = styled.div`background: #111827; border: 1px solid #1e2030; border-radius: 14px; overflow-x: auto; animation: ${fadeIn} 0.5s ease;`;
const TH = styled.div`
  display: grid; grid-template-columns: 120px 100px 1fr 100px 100px 100px 100px; gap: 8px;
  padding: 12px 16px; background: #0d1117; font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase;
`;
const TR = styled.div`
  display: grid; grid-template-columns: 120px 100px 1fr 100px 100px 100px 100px; gap: 8px;
  padding: 16px; border-bottom: 1px solid #1e2030; font-size: 13px; font-weight: 500;
  &:hover { background: #1a1f2e; }
  &:last-child { border-bottom: none; }
`;

const Badge = styled.span`
  padding: 4px 8px; border-radius: 6px; font-size: 10px; font-weight: 700;
  background: ${p => p.type === 'BUY' ? 'rgba(16,185,129,0.15)' : 'rgba(244,63,94,0.15)'};
  color: ${p => p.type === 'BUY' ? '#10b981' : '#f43f5e'};
`;

function VwapHistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiService.axiosInstance.get('/api/vwap/history');
        if (data.success) setHistory(data.history);
      } catch (e) {}
    })();
  }, []);

  const fmt = n => n != null ? Number(n).toLocaleString() : '0';
  const fmtDate = d => {
    const t = new Date(d);
    return `${t.getMonth()+1}/${t.getDate()} ${String(t.getHours()).padStart(2,'0')}:${String(t.getMinutes()).padStart(2,'0')}`;
  };

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>매매 내역 (History)</PageTitle>
        </TitleArea>
        <NavTabs>
          <NavTab onClick={() => navigate('/labs/vwap')}>대시보드</NavTab>
          <NavTab onClick={() => navigate('/labs/vwap/positions')}>포지션</NavTab>
          <NavTab active>매매내역</NavTab>
        </NavTabs>
      </TopBar>

      <Table>
        <TH><span>시간</span><span>동작</span><span>종목</span><span>체결가</span><span>수량</span><span>사유</span><span>수익률</span></TH>
        {history.map(h => (
          <TR key={h.id}>
            <span style={{ color: '#94a3b8', fontSize: 11 }}>{fmtDate(h.timestamp || h.sellTime)}</span>
            <div><Badge type={h.action}>{h.action}</Badge></div>
            <div>
              <div style={{ color: '#fff', fontWeight: 600 }}>{h.name}</div>
            </div>
            <span>₩{fmt(h.action === 'BUY' ? h.price : h.sellPrice)}</span>
            <span>{fmt(h.shares)}주</span>
            <span style={{ color: '#fb923c', fontSize: 11, fontWeight: 700 }}>{h.reason}</span>
            {h.action === 'SELL' ? (
              <span style={{ color: h.returnRate > 0 ? '#10b981' : '#f43f5e', fontWeight: 800 }}>
                {h.returnRate > 0 ? '+' : ''}{h.returnRate}%
              </span>
            ) : <span>-</span>}
          </TR>
        ))}
        {history.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>매매 내역이 없습니다.</div>
        )}
      </Table>
    </Page>
  );
}

export default VwapHistoryPage;
